import React, { useRef, useEffect } from 'react';
import { CompositeMapController } from '../../lib/map/CompositeMapController';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { IPlan } from '../../types/plans';
import { IMapController, IMapData } from '../../lib/map';

interface MapProps {
  plan: IPlan;
  onChange: (data: IMapData) => void;
  onLoad: (value: boolean) => void;
  refController: React.MutableRefObject<IMapController | null>;
  className?: string;
  style?: React.CSSProperties;
  isMobile?: boolean;
}

const Map = ({
  plan,
  onChange,
  onLoad,
  refController,
  className,
  style,
  isMobile = false,
}: MapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<IMapController | null>(null);

  // Initialize map controller only once
  useEffect(() => {
    if (!map.current) {
      const controller = new CompositeMapController({
        ref: mapContainer,
        plan,
        isMobile,
        features: {
          markers: false,
          lines: false,
          polygons: false,
          geocoder: true,
          history: false,
        },
      });
      controller.enableGeocoder();
      if (onChange) controller.setHandler(onChange);
      if (onLoad) controller.onLoad = onLoad;
      refController.current = controller;
      map.current = controller;
    }
  }, [isMobile]); // Only recreate if isMobile changes

  // Handle plan changes separately
  useEffect(() => {
    if (map.current && plan) {
      map.current.setPlan(plan);
    }
  }, [plan]);

  useEffect(() => {
    const handleResize = () => {
      if (map.current) {
        window.setTimeout(() => {
          map.current?.getMap().resize();
        }, 100);
      }
    };

    const handleVisualViewportResize = () => {
      handleResize();
    };

    window.addEventListener('resize', handleResize);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      }
    };
  }, []);

  const mapStyles: React.CSSProperties = {
    height: '100%',
    width: '100%',
    ...style,
  };

  return <div ref={mapContainer} style={mapStyles} className={className} />;
};

export default Map;
